// ----------------------------------------------------------
// © Copyright 2021 European Space Agency, 2021
// This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this [source code/executable] package. No part of the package, including this file, may be copied, modified, propagated, or distributed except according to the terms contained in the file ‘LICENSE.txt’.
// -----------------------------------------------------------
import { isDefined, isUndefined } from "../../helpers/check";
import { formatLogs } from "../../helpers/format";
import { datalabToInstanceProperty } from "../../helpers/utils";
import { getServiceContainer } from "../../service-container";
import { datalabsState } from "../../store";
import { DatalabCategory, } from "../../types/datalab-types";
import { Logger } from "../logger";
const logger = new Logger("service", "build");
export class BuildService {
    constructor() {
        this.apiService = getServiceContainer().resolve('IDLDApiService');
        this.datalabService = getServiceContainer().resolve('DatalabService');
        this.state = getServiceContainer().resolve('IState');
    }
    /**
     * TODO This method will fetch the datalab build list from the store for a fast access and then
     * if not available it will fetch from the server and store it
     * if the data are too old
     * @returns a list of datalab builds
     */
    async getDatalabBuilds(datalabId) {
        logger.info(`fetching builds for datalabBuildId ${datalabId}`);
        //TODO: check with others if this is ok, BE uses variable build_id but FE uses datalabId
        return await this.apiService.getBuilds(datalabId);
    }
    async getBuild(buildId) {
        logger.info(`fetching build ${buildId}`);
        // TODO implement a cache here, we are getting the build too many times
        const build = await this.apiService.getBuild(buildId);
        return formatLogs(build);
    }
    /**
     * Triggers a build of a datalab
     * Watches for the build status and update if build success
     * @param datalab
     * @returns
     */
    async buildLab(datalab) {
        const labBuild = await this.apiService.buildDatalab(datalab.id);
        return labBuild;
    }
    async scanBuild(buildId) {
        // TODO a cache mechanism here will allow to return only the scanRef which allow to fetch the build status
        // log of the scan
        // prevent for running multiple scan
        return await this.apiService.scanDatalabBuild(buildId);
    }
    /**
     * Launches a datalab with a runtimeConfiguration using the Helm chart
     * It duplicates the card and put it in the running section
     * Trigger a run on the server side and if the run failed to start, remove from the list
     * @param datalab
     * @param runtimeConfiguration
     * @returns
     */
    runBuildWithHelm(datalab, runtimeConfiguration) {
        var _a;
        // determine the unique identifier for the datalab, counting the number of datalab that are running
        const launchRef = this.createRunningBuild(datalab, runtimeConfiguration);
        const payload = {
            launchRef: launchRef,
            dlpId: datalab.id,
            idAsName: true,
            name: runtimeConfiguration.instanceName,
            isDev: true,
            version: runtimeConfiguration.build.version.split("-")[0],
            image: `datalabs/${datalab.id}`,
            build: runtimeConfiguration.build.id,
            volumes: runtimeConfiguration.volumes,
            resources: runtimeConfiguration.resources,
            clusterId: (_a = this.state.cluster) === null || _a === void 0 ? void 0 : _a.id,
        };
        // start the datalab this could be long
        return this.apiService.createDatalabInstance(payload).then((buildRun) => {
            logger.info(`Datalab instance created (${launchRef})`);
            const runningInstance = datalabToInstanceProperty([runtimeConfiguration.build])(buildRun);
            this.updateRunningBuild(launchRef, runningInstance);
            // TODO better to use the constants but they are defined in a higher level of code
            window.dispatchEvent(new CustomEvent("datalabCreating", { detail: runningInstance }));
            return runningInstance;
        }, (reason) => {
            // removing the datalab from the list
            this.deleteRunningBuild(launchRef);
            logger.error(`Failed to start for:`, reason);
            return Promise.reject(reason);
        });
    }
    /**
     * Method to stop a datalab started with helm chart
     * @param datalab
     * @returns
     */
    async stopRunningBuild(datalab) {
        if (isUndefined(datalab.runningStatus)) {
            throw new Error("The datalab is not running please check your input");
        }
        return this.apiService.destroyDatalab(datalab.runningStatus.id).then((done) => {
            // Emit anyway in case websocket is down
            logger.info("destroy datalab response", done);
            if (done === true) {
                this.deleteRunningBuild(datalab.runningStatus.id);
                window.dispatchEvent(new CustomEvent("datalabDestroyed", { detail: datalab.runningStatus }));
                return done;
            }
            else {
                logger.error("Failed to destroy datalab, backend logs need to be checked");
                return Promise.reject(`Couldn't delete the selected datalab.<br/>
        This event has been logged for operator analysis<br/>
        Please try again later`);
            }
        });
    }
    updateRunningBuild(runningId, runningInstance) {
        const running = datalabsState.running.map((datalab) => {
            if (datalab.runningStatus.launchRef === runningId) {
                logger.debug(`Datalab instance status update (${runningInstance.id})`, runningInstance);
                datalab.runningStatus = runningInstance;
            }
            return datalab;
        });
        datalabsState.running = [...running];
    }
    deleteRunningBuild(runningId) {
        datalabsState.running = datalabsState.running.filter((datalab) => datalab.runningStatus.id !== runningId);
    }
    createRunningBuild(datalab, runtimeConfiguration) {
        const runningId = `${Math.floor((1 + Math.random()) * Math.pow(10, 10))}`;
        // clone the datalab in the running section with the right status
        // We put the same id and launchRef, id will be replaced by the server and launchRef should remain to map
        // our local reference
        const runningStatus = {
            id: runningId,
            launchRef: runningId,
            build: runtimeConfiguration.build.id,
            status: "waiting" /* RunningState.waiting */,
            name: runtimeConfiguration.instanceName,
            resources: [],
            datalabName: datalab.name,
            description: datalab.description,
            categories: datalab.runningStatus ? datalab.runningStatus.categories : DatalabCategory.User
        };
        const clone = Object.assign(Object.assign({}, JSON.parse(JSON.stringify(datalab))), { runtimeConfiguration, runningStatus });
        logger.debug(`Running datalab cloned (${runningId})`, clone);
        // Looks non-optimized but this is the only way we have to ensure a repaint of the list
        datalabsState.running = [...datalabsState.running, clone];
        return runningId;
    }
    /**
     * Publish the build of the datalab
     * @param datalab
     * @param build
     * @returns
     */
    async publishBuild(datalab, build) {
        try {
            const publishId = await this.apiService.publishDatalabBuild(build.id);
            if (isDefined(publishId)) {
                this.datalabService.refreshDatalab(datalab);
            }
            return publishId;
        }
        catch (e) {
            logger.error("Failed to publish", e);
        }
    }
    /**
     * Handle the revoke moderation request, to remove from the moderation state
     * @param datalab
     * @param build
     * @returns
     */
    async revokeModeration(datalab, build) {
        const publishId = await this.apiService.revokeBuildModeration(build.id);
        if (isDefined(publishId)) {
            this.datalabService.refreshDatalab(datalab);
        }
        return publishId;
    }
}
